import {
  AccountBalance as AccountingHubIcon,
  AdminPanelSettings as AdminHubIcon,
  HealthAndSafety as SafetyHubIcon,
  LocalShipping as FleetHubIcon,
} from "@mui/icons-material";
import React from "react";
import { Navigate, useParams } from "react-router-dom";
import { useGetSubscriptionLevelQuery } from "../../api/subscriptionApi";
import { useAppSelector } from "../../app/hooks";
import PageTitle from "../../components/common/PageTitle";
import PageLayout from "../../components/layout/PageLayout";
import {
  getHubShortcuts,
  getVisibleNavItems,
  HUB_META,
  HUB_PATHS,
  NavHubId,
} from "../../components/layout/navMenu";
import FleetHubOverview from "./FleetHubOverview";
import SafetyHubOverview from "./SafetyHubOverview";
import AccountingHubOverview from "./AccountingHubOverview";
import AdminHubOverview from "./AdminHubOverview";

const HUB_ICONS: Record<NavHubId, React.ReactNode> = {
  fleet: <FleetHubIcon />,
  safety: <SafetyHubIcon />,
  accounting: <AccountingHubIcon />,
  admin: <AdminHubIcon />,
};

const isNavHubId = (value: string | undefined): value is NavHubId =>
  value === "fleet" ||
  value === "safety" ||
  value === "accounting" ||
  value === "admin";

const SectionHubPage: React.FC = () => {
  const { section } = useParams<{ section: string }>();
  const user = useAppSelector((state) => state.auth.user);
  const { data: subscriptionLevelData } = useGetSubscriptionLevelQuery(
    undefined,
    { skip: !user, refetchOnMountOrArgChange: true },
  );
  const subscriptionLevel = subscriptionLevelData?.level ?? null;

  if (!isNavHubId(section)) {
    return <Navigate to="/dashboard" replace />;
  }

  const items = getVisibleNavItems(user, subscriptionLevel);
  const shortcuts = getHubShortcuts(items, HUB_PATHS[section]);
  const meta = HUB_META[section];

  if (shortcuts.length === 0) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <PageLayout variant="constrained" maxWidth={1200}>
      <PageTitle
        title={meta.title}
        subtitle={meta.subtitle}
        icon={HUB_ICONS[section]}
      />

      {section === "fleet" ? (
        <FleetHubOverview
          shortcuts={shortcuts}
          subscriptionLevel={subscriptionLevel}
        />
      ) : section === "safety" ? (
        <SafetyHubOverview
          shortcuts={shortcuts}
          subscriptionLevel={subscriptionLevel}
        />
      ) : section === "accounting" ? (
        <AccountingHubOverview shortcuts={shortcuts} />
      ) : (
        <AdminHubOverview
          shortcuts={shortcuts}
          subscriptionLevel={subscriptionLevel}
        />
      )}
    </PageLayout>
  );
};

export default SectionHubPage;
